import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-fast-protocol-mediterranean-questions',
  templateUrl: './fast-protocol-mediterranean-questions.component.html',
  styleUrl: './fast-protocol-mediterranean-questions.component.css',
  standalone: false,
})
export class FastProtocolMediterraneanQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  ngOnInit() {}

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges(); // Ensure UI updates immediately
  }

  // Put the real question names here:
  private toggleRules: Record<
    string,
    {
      normalClears?: string[];
      normalDisables?: string[];
      abnormalClears?: string[];
      disableOnNull?: string[];
    }
  > = {
    question40: {
      normalClears: ['question41'],
      normalDisables: ['question47'],
    },
    question41: {
      abnormalClears: ['question40'],
      disableOnNull: ['question47'],
    },
    question42: {
      normalClears: ['question43'],
      normalDisables: ['question48'],
    },
    question43: {
      abnormalClears: ['question42'],
      disableOnNull: ['question48'],
    },
    question44: { normalClears: ['question46'] },
    question46: { abnormalClears: ['question44'] },
  };

  private norm(v: any): string {
    return String(v ?? '')
      .trim()
      .toLowerCase();
  }

  private isNormalSelected(
    element: any,
    value: any,
    choiceText?: string,
  ): boolean {
    const text =
      choiceText ??
      element?.choices?.find((c: any) => c.value === value)?.text ??
      '';
    return this.norm(text) === this.norm(this.translate.instant('Normal'));
  }

  private isAbnormalSelected(
    element: any,
    value: any,
    choiceText?: string,
  ): boolean {
    const text =
      choiceText ??
      element?.choices?.find((c: any) => c.value === value)?.text ??
      '';
    return this.norm(text) === this.norm(this.translate.instant('Abnormal'));
  }
  // Track selected text per control
  private selectedTexts: Record<string, string> = {};

  onRadioClick(
    controlName: string,
    value: any,
    element?: any,
    choiceText?: string,
  ): void {
    if (this.readonlyMode) return;

    const control = this.formGroup.get(controlName);
    if (!control) return;

    const rules = this.toggleRules[controlName];

    // Toggle off: clicking the already-selected value deselects it
    if (
      control.value === value &&
      this.selectedTexts[controlName] === choiceText
    ) {
      control.setValue(null);
      delete this.selectedTexts[controlName];
      if (rules) {
        // Re-enable mutual-exclusion peers (they were cleared but stayed enabled)
        for (const t of [
          ...(rules.normalClears ?? []),
          ...(rules.abnormalClears ?? []),
        ]) {
          this.enable(t);
        }
        // Re-enable sub-questions that were disabled by Normal
        for (const t of rules.normalDisables ?? []) this.enable(t);
        // Disable sub-questions that are only valid when this question is active
        for (const t of rules.disableOnNull ?? []) this.disableAndClear(t);
      }
      this.cdr.detectChanges();
      return;
    }

    this.selectedTexts[controlName] = choiceText ?? '';
    control.setValue(value);

    if (!rules) {
      this.cdr.detectChanges();
      return;
    }

    // Clean slate: re-enable everything this control's rules might have previously
    // disabled, so switching between choices always starts from a clean state.
    for (const t of [
      ...(rules.normalClears ?? []),
      ...(rules.normalDisables ?? []),
      ...(rules.abnormalClears ?? []),
      ...(rules.disableOnNull ?? []),
    ]) {
      this.enable(t);
    }

    const isNormal = this.isNormalSelected(element, value, choiceText);
    const isAbnormal = this.isAbnormalSelected(element, value, choiceText);

    if (isNormal) {
      for (const t of rules.normalClears ?? []) this.clearOnly(t);
      for (const t of rules.normalDisables ?? []) this.disableAndClear(t);
      // Sub-questions gated on Abnormal must also be disabled when Normal is selected
      for (const t of rules.disableOnNull ?? []) this.disableAndClear(t);
    }

    if (isAbnormal) {
      for (const t of rules.abnormalClears ?? []) this.clearOnly(t);
      // disableOnNull targets stay enabled — they are the sub-questions for the Abnormal path
    }

    this.cdr.detectChanges();
  }

  private clearOnly(controlName: string): void {
    const c = this.formGroup.get(controlName);
    if (!c) return;
    c.setValue(null, { emitEvent: false });
    c.enable({ emitEvent: false }); // make sure it's enabled
  }

  private disableAndClear(controlName: string): void {
    const c = this.formGroup.get(controlName);
    if (!c) return;
    c.setValue(null, { emitEvent: false });
    c.disable({ emitEvent: false });
  }

  private enable(controlName: string): void {
    const c = this.formGroup.get(controlName);
    if (!c) return;
    c.enable({ emitEvent: false });
  }
}
